import { useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import * as yup from "yup";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import CustomSpinner from "../../../../components/spinner/CustomSpinner";
import { namespaces } from "../../../../i18n/i18n.constants";
import { useChangePasswordMutation } from "../../../../app/api/userApi";
import "./ChangePasswordModal.scss";

interface Props {
    show: boolean;
    onHide: () => void;
}

const ChangePasswordModal = ({ show, onHide }: Props) => {
    const { t } = useTranslation([namespaces.auth]);
    const [changePassword, { isLoading }] = useChangePasswordMutation();

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const toggleShowCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
    const toggleShowNewPassword = () => setShowNewPassword(!showNewPassword);
    const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    const currentPasswordFieldName = "password";
    const newPasswordFieldName = "newPassword";
    const confirmPasswordFieldName = "confirmPassword";

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            backdropClassName="blur-backdrop"
            fullscreen="sm-down"
            className="change-password-modal"
            contentClassName="change-password-modal-content"
            aria-labelledby="change-password-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("changePassword.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <CustomSpinner hideTextTitle={true} />
                ) : (
                    <>
                        <p className="mb-4">{t("changePassword.description")}</p>
                        <Formik
                            validationSchema={yup.object().shape({
                                [currentPasswordFieldName]: yup
                                    .string()
                                    .required(t("required.field", { ns: namespaces.validation })),
                                [newPasswordFieldName]: yup
                                    .string()
                                    .required(t("required.field", { ns: namespaces.validation }))
                                    .min(8, t("minPassword.field", { ns: namespaces.validation, count: 8 }))
                                    .matches(
                                        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/,
                                        t("passwordRequirements.field", { ns: namespaces.validation })
                                    ),
                                [confirmPasswordFieldName]: yup
                                    .string()
                                    .required(t("required.field", { ns: namespaces.validation }))
                                    .oneOf(
                                        [yup.ref(newPasswordFieldName)],
                                        t("passwordsMustMatch.field", { ns: namespaces.validation })
                                    ),
                            })}
                            initialValues={{
                                [currentPasswordFieldName]: "",
                                [newPasswordFieldName]: "",
                                [confirmPasswordFieldName]: "",
                            }}
                            onSubmit={async (values, { setSubmitting, resetForm }) => {
                                try {
                                    await changePassword({
                                        password: values[currentPasswordFieldName],
                                        newPassword: values[newPasswordFieldName],
                                        confirmNewPassword: values[confirmPasswordFieldName],
                                    }).unwrap();
                                    resetForm();
                                    onHide();
                                } catch (error) {
                                    console.error("Change password error:", error);
                                }
                                setSubmitting(false);
                            }}
                        >
                            {({ handleSubmit, handleChange, handleBlur, isSubmitting, values, touched, errors }) => (
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3" controlId={currentPasswordFieldName}>
                                        <Form.Label className="custom-form-label">
                                            {t("changePassword.currentPassword")}
                                        </Form.Label>
                                        <InputGroup className="custom-input-group">
                                            <Form.Control
                                                name={currentPasswordFieldName}
                                                className="custom-form-control"
                                                type={showCurrentPassword ? "text" : "password"}
                                                aria-label={t("changePassword.currentPassword")}
                                                value={values[currentPasswordFieldName]}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isValid={
                                                    touched[currentPasswordFieldName] &&
                                                    !errors[currentPasswordFieldName]
                                                }
                                                isInvalid={
                                                    touched[currentPasswordFieldName] &&
                                                    !!errors[currentPasswordFieldName]
                                                }
                                            />
                                            <InputGroup.Text
                                                className="password-hiding-block"
                                                onClick={toggleShowCurrentPassword}
                                            >
                                                {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                                            </InputGroup.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors[currentPasswordFieldName]}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId={newPasswordFieldName}>
                                        <Form.Label className="custom-form-label">
                                            {t("changePassword.newPassword")}
                                        </Form.Label>
                                        <InputGroup className="custom-input-group">
                                            <Form.Control
                                                name={newPasswordFieldName}
                                                className="custom-form-control"
                                                type={showNewPassword ? "text" : "password"}
                                                aria-label={t("changePassword.newPassword")}
                                                value={values[newPasswordFieldName]}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isValid={touched[newPasswordFieldName] && !errors[newPasswordFieldName]}
                                                isInvalid={
                                                    touched[newPasswordFieldName] && !!errors[newPasswordFieldName]
                                                }
                                            />
                                            <InputGroup.Text
                                                className="password-hiding-block"
                                                onClick={toggleShowNewPassword}
                                            >
                                                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                            </InputGroup.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors[newPasswordFieldName]}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId={confirmPasswordFieldName}>
                                        <Form.Label className="custom-form-label">
                                            {t("changePassword.confirmNewPassword")}
                                        </Form.Label>
                                        <InputGroup className="custom-input-group">
                                            <Form.Control
                                                name={confirmPasswordFieldName}
                                                className="custom-form-control"
                                                type={showConfirmPassword ? "text" : "password"}
                                                aria-label={t("changePassword.confirmNewPassword")}
                                                value={values[confirmPasswordFieldName]}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isValid={
                                                    touched[confirmPasswordFieldName] &&
                                                    !errors[confirmPasswordFieldName]
                                                }
                                                isInvalid={
                                                    touched[confirmPasswordFieldName] &&
                                                    !!errors[confirmPasswordFieldName]
                                                }
                                            />
                                            <InputGroup.Text
                                                className="password-hiding-block"
                                                onClick={toggleShowConfirmPassword}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </InputGroup.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors[confirmPasswordFieldName]}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>

                                    <div className="d-flex justify-content-end mt-4">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={onHide}
                                            className="me-2 p-3"
                                            disabled={isSubmitting}
                                        >
                                            {t("buttons.cancel", { ns: namespaces.common })}
                                        </Button>
                                        <Button className="p-3" variant="primary" type="submit" disabled={isSubmitting}>
                                            {t("changePassword.sendBtn")}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ChangePasswordModal;
